import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchUpdates } from "redux/logistics/updates/updatesSlice";
import { useDebouncedValue } from "utils/logic";

const DEFAULT_PENDING_FILTERS = {
    type: "user",
};

const UPDATE_TYPE_OPTIONS = [
    { value: "all", label: "All" },
    { value: "user", label: "User" },
    { value: "auto", label: "Auto" },
    { value: "system", label: "System" },
    { value: "scraper", label: "Scraper" },
];

const sanitizeFilters = (filters) =>
    Object.entries(filters).reduce((acc, [key, value]) => {
        if (value === null || value === undefined) return acc;
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return acc;
            acc[key] = trimmed;
            return acc;
        }
        acc[key] = value;
        return acc;
    }, {});

const parseNextPageFromUrl = (nextUrl) => {
    if (!nextUrl) return null;
    try {
        const parsed = new URL(nextUrl, window.location.origin);
        const value = Number(parsed.searchParams.get("page"));
        return Number.isFinite(value) && value > 0 ? value : null;
    } catch {
        return null;
    }
};

const formatTarget = (update) => {
    const orderNumber = update?.order_no
        ? `${update.order_no}${update?.order_group ? ` ${update.order_group}` : ""}${update?.backorder ? ` ${update.backorder}` : ""}`
        : null;
    const busName = update?.bus_name || null;
    if (orderNumber && busName) return `${orderNumber} | ${busName}`;
    if (orderNumber) return orderNumber;
    if (busName) return busName;
    return "-";
};

export const UpdatesTab = () => {
    const dispatch = useDispatch();
    const { updates, loading, error, count, next } = useSelector((state) => state.updates);
    const [page, setPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState(() => sanitizeFilters(DEFAULT_PENDING_FILTERS));
    const [pendingFilters, setPendingFilters] = useState(DEFAULT_PENDING_FILTERS);
    const loadMoreRef = useRef(null);
    const pagingRef = useRef(false);
    const debouncedFilters = useDebouncedValue(pendingFilters, 500);

    useEffect(() => {
        dispatch(fetchUpdates({ page, filters: activeFilters, append: page > 1 }));
    }, [dispatch, page, activeFilters]);

    useEffect(() => {
        if (!loading) pagingRef.current = false;
    }, [loading]);

    useEffect(() => {
        const sanitized = sanitizeFilters(debouncedFilters);
        if (JSON.stringify(sanitized) === JSON.stringify(activeFilters)) return;
        setActiveFilters(sanitized);
        pagingRef.current = false;
        setPage(1);
    }, [debouncedFilters, activeFilters]);

    useEffect(() => {
        if (!loadMoreRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                if (!next || loading || pagingRef.current) return;
                const nextPage = parseNextPageFromUrl(next);
                if (!nextPage || nextPage === page) return;
                pagingRef.current = true;
                setPage(nextPage);
            },
            { rootMargin: "200px" }
        );
        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [next, loading, page]);

    const handleFilterChange = (key, value) => {
        setPendingFilters((prev) => ({ ...prev, [key]: value }));
    };

    return (
        <div className="trips-page">
            <header className="trips-header">
                <p className="trips-subtitle">Search and review update history across orders and buses.</p>
                <div className="trips-summary">
                    <span>Total: {count || 0}</span>
                    <span>Loaded: {updates.length}</span>
                </div>
            </header>

            <section className="trips-filters">
                <div className="trips-filters__grid">
                <label className="trips-filter">
                    <span className="trips-filter__label">Order #</span>
                    <input
                        type="text"
                        className="trips-filter__input"
                        value={pendingFilters.order_no || ""}
                        onChange={(event) => handleFilterChange("order_no", event.target.value)}
                        placeholder="Search by order number"
                    />
                </label>
                <label className="trips-filter">
                    <span className="trips-filter__label">User</span>
                    <input
                        type="text"
                        className="trips-filter__input"
                        value={pendingFilters.user || ""}
                        onChange={(event) => handleFilterChange("user", event.target.value)}
                        placeholder="Username or name"
                    />
                </label>
                <label className="trips-filter">
                    <span className="trips-filter__label">Message</span>
                    <input
                        type="text"
                        className="trips-filter__input"
                        value={pendingFilters.message || ""}
                        onChange={(event) => handleFilterChange("message", event.target.value)}
                        placeholder="Contains text"
                    />
                </label>
                <label className="trips-filter">
                    <span className="trips-filter__label">Update Type</span>
                    <select
                        className="trips-filter__input"
                        value={pendingFilters.type || "user"}
                        onChange={(event) => handleFilterChange("type", event.target.value)}
                    >
                        {UPDATE_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="trips-filter">
                    <span className="trips-filter__label">Start date</span>
                    <input
                        type="date"
                        className="trips-filter__input"
                        value={pendingFilters.start_date || ""}
                        onChange={(event) => handleFilterChange("start_date", event.target.value)}
                    />
                </label>
                <label className="trips-filter">
                    <span className="trips-filter__label">End date</span>
                    <input
                        type="date"
                        className="trips-filter__input"
                        value={pendingFilters.end_date || ""}
                        onChange={(event) => handleFilterChange("end_date", event.target.value)}
                    />
                </label>
                </div>
            </section>

            <section className="trips-list">
                {loading && page === 1 && <div className="trips-status">Loading updates...</div>}
                {error && !loading && <div className="trips-status trips-status--error">{String(error)}</div>}
                {!loading && !error && updates.length === 0 && <div className="trips-status">No updates found.</div>}
                {!error && updates.length > 0 && (
                    <div className="trips-table trips-table--updates">
                        <div className="trips-table__head">
                            <div>Target</div>
                            <div>Message</div>
                            <div>User</div>
                            <div>Type</div>
                            <div>Timestamp</div>
                        </div>
                        <div className="trips-table__body">
                            {updates.map((update) => (
                                <div key={update.id} className="trips-table__row">
                                    <div>{formatTarget(update)}</div>
                                    <div>{update.message || "-"}</div>
                                    <div>{update.username || "-"}</div>
                                    <div>{update.type || "-"}</div>
                                    <div>{update.created_at || "-"}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>
            {loading && page > 1 && <div className="trips-status">Loading more updates...</div>}
            <div ref={loadMoreRef} className="trips-scroll-sentinel" aria-hidden="true" />
        </div>
    );
};
